// localJsonStore.ts
// In-memory cache + debounced/idle localStorage persistence for large JSON payloads.
//
// Why:
// - JSON.parse / JSON.stringify of big arrays (invoices/items/parties) blocks the main thread.
// - Caching reduces repeated parsing, and debounced idle flush moves stringify off critical clicks.

type IdleDeadlineLike = {
  didTimeout: boolean
  timeRemaining: () => number
}

type RequestIdleCallbackLike = (cb: (deadline: IdleDeadlineLike) => void, opts?: { timeout: number }) => number
type CancelIdleCallbackLike = (handle: number) => void

type Entry = {
  value: unknown
  dirty: boolean
  debounceMs: number
  timeoutHandle: number | null
  idleHandle: number | null
}

const DEFAULT_DEBOUNCE_MS = 600
const IDLE_TIMEOUT_MS = 2000

const hasWindow = typeof window !== 'undefined'
const hasDocument = typeof document !== 'undefined'

const requestIdleCallbackLike: RequestIdleCallbackLike | null = hasWindow && 'requestIdleCallback' in window
  ? (window.requestIdleCallback as unknown as RequestIdleCallbackLike).bind(window)
  : null

const cancelIdleCallbackLike: CancelIdleCallbackLike | null = hasWindow && 'cancelIdleCallback' in window
  ? (window.cancelIdleCallback as unknown as CancelIdleCallbackLike).bind(window)
  : null

const entries = new Map<string, Entry>()

let listenersBound = false

const bindFlushListenersOnce = () => {
  if (!hasWindow || !hasDocument || listenersBound) return
  listenersBound = true

  const flush = () => {
    try {
      flushAllLocalJSON()
    } catch {
      // best-effort
    }
  }

  window.addEventListener('beforeunload', flush)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush()
  })
}

const safeParse = <T>(raw: string | null, fallback: T): T => {
  if (!raw) return fallback
  try {
    return JSON.parse(raw) as T
  } catch {
    return fallback
  }
}

const ensureEntry = <T>(key: string, fallback: T): Entry => {
  bindFlushListenersOnce()

  const existing = entries.get(key)
  if (existing) return existing

  const value = hasWindow ? safeParse<T>(window.localStorage.getItem(key), fallback) : fallback
  const entry: Entry = {
    value,
    dirty: false,
    debounceMs: DEFAULT_DEBOUNCE_MS,
    timeoutHandle: null,
    idleHandle: null
  }
  entries.set(key, entry)
  return entry
}

const scheduleFlush = (key: string, debounceMs: number) => {
  if (!hasWindow) return
  const entry = entries.get(key)
  if (!entry) return

  // Clear previous schedules
  if (entry.timeoutHandle !== null) {
    window.clearTimeout(entry.timeoutHandle)
    entry.timeoutHandle = null
  }
  if (entry.idleHandle !== null && cancelIdleCallbackLike) {
    cancelIdleCallbackLike(entry.idleHandle)
    entry.idleHandle = null
  }

  entry.timeoutHandle = window.setTimeout(() => {
    entry.timeoutHandle = null
    if (!entry.dirty) return

    if (requestIdleCallbackLike) {
      entry.idleHandle = requestIdleCallbackLike(
        () => {
          entry.idleHandle = null
          flushLocalJSON(key)
        },
        { timeout: IDLE_TIMEOUT_MS }
      )
      return
    }

    // Fallback: flush on timeout (still better than doing it inside click handlers).
    flushLocalJSON(key)
  }, debounceMs)
}

export const getLocalJSON = <T>(key: string, fallback: T): T => {
  const entry = ensureEntry<T>(key, fallback)
  return entry.value as T
}

// Returns the cached value reference for in-place mutation.
// Call markLocalJSONDirty(key) after mutating.
export const getMutableLocalJSON = <T>(key: string, fallback: T): T => {
  return getLocalJSON<T>(key, fallback)
}

export const setLocalJSON = <T>(
  key: string,
  value: T,
  opts?: { debounceMs?: number; immediate?: boolean }
): void => {
  const entry = ensureEntry<T>(key, value)
  entry.value = value
  entry.dirty = true

  const debounceMs = Number.isFinite(opts?.debounceMs as number)
    ? (opts!.debounceMs as number)
    : entry.debounceMs
  entry.debounceMs = debounceMs

  if (opts?.immediate) {
    flushLocalJSON(key)
    return
  }

  scheduleFlush(key, debounceMs)
}

export const markLocalJSONDirty = (
  key: string,
  opts?: { debounceMs?: number; immediate?: boolean }
): void => {
  const entry = entries.get(key)
  if (!entry) return
  entry.dirty = true

  const debounceMs = Number.isFinite(opts?.debounceMs as number)
    ? (opts!.debounceMs as number)
    : entry.debounceMs
  entry.debounceMs = debounceMs

  if (opts?.immediate) {
    flushLocalJSON(key)
    return
  }

  scheduleFlush(key, debounceMs)
}

export const flushLocalJSON = (key: string): void => {
  if (!hasWindow) return
  const entry = entries.get(key)
  if (!entry || !entry.dirty) return

  // Clear scheduled work (we're flushing now).
  if (entry.timeoutHandle !== null) {
    window.clearTimeout(entry.timeoutHandle)
    entry.timeoutHandle = null
  }
  if (entry.idleHandle !== null && cancelIdleCallbackLike) {
    cancelIdleCallbackLike(entry.idleHandle)
    entry.idleHandle = null
  }

  entry.dirty = false
  try {
    window.localStorage.setItem(key, JSON.stringify(entry.value))
  } catch {
    // best-effort: avoid re-trying in a tight loop if quota is exceeded
  }
}

export const flushAllLocalJSON = (): void => {
  for (const key of entries.keys()) {
    flushLocalJSON(key)
  }
}

export const invalidateLocalJSON = (key: string): void => {
  const entry = entries.get(key)
  if (!entry) return

  if (hasWindow && entry.timeoutHandle !== null) {
    window.clearTimeout(entry.timeoutHandle)
  }
  if (entry.idleHandle !== null && cancelIdleCallbackLike) {
    cancelIdleCallbackLike(entry.idleHandle)
  }

  entries.delete(key)
}

